import { ioListOptions, ioSaveOption } from '@/febos/servicios/api/herramientas.api';
import { uuid } from 'vue-uuid';
import OptionTreeMixin from '../componentes/options/OptionTreeMixin';

const treeMixin = OptionTreeMixin.methods;

/**
 * Loads the parameter tree from the API and transforms it into a hierarchical structure.
 * @returns {Promise<Array>} Tree nodes array
 */
export async function loadTree() {
  try {
    const response = await ioListOptions({
      grupoOpcion: 'parametros',
      filtros: 'deshabilitado:0'
    });
    return treeMixin.optionToTree(response.data.opciones || []);
  } catch (error) {
    throw new Error(`Error al cargar el árbol de parámetros: ${error.message}`);
  }
}

/**
 * Saves a folder (create or edit) based on presence of optionId.
 * - Creation: generates UUID v4, includes padre and orden
 * - Edition: uses existing optionId, only updates descripcion
 * @param {Object} data - Folder data
 * @param {string} [data.optionId] - Existing option ID (edit mode)
 * @param {string} [data.parameterId] - Existing parameter ID (edit mode)
 * @param {string} data.name - Folder name
 * @param {string} [data.parentId] - Parent node ID (create mode)
 * @param {number} [data.order] - Order within parent (create mode)
 * @returns {Promise<void>}
 */
export async function saveFolder({ optionId, parameterId, name, parentId, order }) {
  const isEdit = !!optionId;
  const param = { global: 'si', ...(isEdit && { id: optionId }) };
  const body = {
    grupoId: 'parametros',
    parametroId: isEdit ? parameterId : uuid.v4(),
    descripcion: name,
    ...(!isEdit && { padre: parentId, orden: order }),
    deshabilitado: 0,
    extra: JSON.stringify({ isFolder: true })
  };

  try {
    await ioSaveOption(param, body);
  } catch (error) {
    throw new Error(`Error al guardar carpeta "${name}": ${error.message}`);
  }
}

/**
 * Saves a parameter (create or edit) based on presence of optionId.
 * - Creation: generates UUID v1, includes padre and orden
 * - Edition: uses existing optionId, updates descripcion and parameters
 * @param {Object} data - Parameter data
 * @param {string} [data.optionId] - Existing option ID (edit mode)
 * @param {string} [data.parameterId] - Existing parameter ID (edit mode)
 * @param {string} data.name - Parameter name
 * @param {string} [data.parentId] - Parent node ID (create mode)
 * @param {number} [data.order] - Order within parent (create mode)
 * @param {Array} [data.parameters] - Sub-parameters list
 * @returns {Promise<void>}
 */
export async function saveParameter({ optionId, parameterId, name, parentId, order, parameters }) {
  const isEdit = !!optionId;
  const param = { global: 'si', ...(isEdit && { id: optionId }) };
  const body = {
    grupoId: 'parametros',
    parametroId: isEdit ? parameterId : uuid.v1(),
    descripcion: name,
    ...(!isEdit && { padre: parentId, orden: order }),
    deshabilitado: 0,
    extra: JSON.stringify({ isFolder: false, parameters })
  };

  try {
    await ioSaveOption(param, body);
  } catch (error) {
    throw new Error(`Error al guardar parámetro "${name}": ${error.message}`);
  }
}

/**
 * Filters a tree structure by text (case-insensitive).
 * Shows matching nodes and their ancestor folders.
 * @param {Array} treeChildren - Tree nodes to filter
 * @param {string} text - Search text
 * @returns {Array} Filtered tree nodes
 */
export const filterTree = treeMixin.filterTree;
